import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getMyTeams,
  getSessions,
  revokeAllSessions,
  revokeSession,
  switchTeam,
} from "../api/TenantApi";
import { loginWithJwt, logout } from "../api/StorageApi";
import { getBaseUrl } from "../api/api";
import { Button } from "../components/ui";
import { LogOut, RefreshCw, Users } from "lucide-react";
import LayoutAction from "../actions/LayoutAction";
import { useDispatch, useSelector } from "react-redux";

export interface MyTeam {
  tenantId: string;
  tenantName: string;
  role: string;
  isDefault: boolean;
}

export interface SessionItem {
  sessionId: string;
  tenantId: string;
  tenantName: string;
  issuedAt: string;
  expiresAt: string;
  lastUsedAt?: string;
  deviceInfo?: string;
  createdBy: string;
}

const TeamAndSessionsSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tenantInfo = useSelector((state: any) => state.tenantReducer?.get?.("tenantInfo"));
  const currentTenantIdFromStore = tenantInfo?.get?.("tenantId") ?? tenantInfo?.tenantId ?? "";
  const [teams, setTeams] = useState<MyTeam[]>([]);
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [revoking, setRevoking] = useState<string | "all" | null>(null);

  const loadTeams = useCallback(async () => {
    setLoadingTeams(true);
    try {
      const data = await getMyTeams();
      setTeams(data);
      if (data.length > 0 && !selectedTeamId) {
        const defaultTeam = data.find((t) => t.isDefault);
        setSelectedTeamId(defaultTeam?.tenantId ?? data[0].tenantId);
      }
    } catch {
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  }, [selectedTeamId]);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const data = await getSessions();
      setSessions(data);
    } catch {
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleSwitchTeam = async () => {
    if (!selectedTeamId) return;
    const currentTenantId =
      currentTenantIdFromStore || (teams.find((t) => t.isDefault)?.tenantId ?? "");
    if (selectedTeamId === currentTenantId) return;
    setSwitching(true);
    try {
      const { token } = await switchTeam(selectedTeamId);
      loginWithJwt(token);
      const baseUrl = getBaseUrl() || "/";
      window.location.href = baseUrl;
    } catch {
      dispatch(
        LayoutAction.setNotification({
          title: t("Team switch"),
          type: "error",
          message: t("Failed to switch team. Please try again."),
        })
      );
      setSwitching(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    setRevoking(sessionId);
    try {
      await revokeSession(sessionId);
      await loadSessions();
      dispatch(
        LayoutAction.setNotification({
          title: t("Sessions"),
          type: "success",
          message: t("Session revoked."),
        })
      );
    } catch {
      dispatch(
        LayoutAction.setNotification({
          title: t("Sessions"),
          type: "error",
          message: t("Failed to revoke session."),
        })
      );
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAll = async () => {
    setRevoking("all");
    try {
      await revokeAllSessions();
      logout();
      dispatch(
        LayoutAction.setNotification({
          title: t("Sessions"),
          type: "success",
          message: t("You have been signed out on all devices."),
        })
      );
      const baseUrl = getBaseUrl() || "/";
      window.location.href = `${baseUrl}/app/login`;
    } catch {
      dispatch(
        LayoutAction.setNotification({
          title: t("Sessions"),
          type: "error",
          message: t("Failed to revoke sessions."),
        })
      );
    } finally {
      setRevoking(null);
    }
  };

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const currentTenantId =
    currentTenantIdFromStore || (teams.find((x) => x.isDefault)?.tenantId ?? "");
  const canSwitch = selectedTeamId && selectedTeamId !== currentTenantId;

  return (
    <div className="p-4 max-w-5xl space-y-8" data-testid="team-and-sessions-setting">
      <section>
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Users className="w-4 h-4" />
          {t("Switch team")}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t("Choose a team to switch to (vendors and admins only).")}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {loadingTeams ? (
            <span className="text-sm text-gray-500">{t("Loading teams…")}</span>
          ) : teams.length === 0 ? (
            <span className="text-sm text-gray-500">{t("No teams available.")}</span>
          ) : (
            <>
              <select
                data-testid="team-switch-select"
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-[200px]"
              >
                {teams.map((team) => (
                  <option key={team.tenantId} value={team.tenantId}>
                    {team.tenantId} - {team.tenantName} {team.isDefault ? `(${t("current")})` : ""}
                  </option>
                ))}
              </select>
              <Button
                data-testid="team-switch-button"
                disabled={!canSwitch || switching}
                onClick={handleSwitchTeam}
              >
                {switching ? t("Switching…") : t("Switch to this team")}
              </Button>
            </>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          {t("Active sessions")}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t("Sessions where you are signed in. You can revoke any session.")}
        </p>
        <div className="mt-4">
          {loadingSessions ? (
            <p className="text-sm text-gray-500">{t("Loading sessions…")}</p>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-gray-500">{t("No active sessions.")}</p>
          ) : (
            <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
              {sessions.map((session) => (
                <li
                  key={session.sessionId}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
                  data-testid={`session-row-${session.sessionId}`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900">{session.tenantName}</span>
                    <span className="text-gray-500 ml-2">({session.tenantId})</span>
                    {session.deviceInfo && (
                      <p className="text-gray-500 truncate mt-0.5">{session.deviceInfo}</p>
                    )}
                    <p className="text-gray-400 text-xs mt-1">
                      {t("Signed in")} {formatDate(session.issuedAt)}
                      {session.lastUsedAt &&
                        ` · ${t("Last used")} ${formatDate(session.lastUsedAt)}`}
                    </p>
                  </div>
                  <Button
                    data-testid={`revoke-session-${session.sessionId}`}
                    variant="secondary"
                    size="sm"
                    disabled={revoking !== null}
                    onClick={() => handleRevokeSession(session.sessionId)}
                  >
                    {revoking === session.sessionId ? t("Revoking…") : t("Revoke")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {sessions.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              data-testid="revoke-all-sessions"
              onClick={handleRevokeAll}
              disabled={revoking !== null}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-800 disabled:opacity-50"
            >
              <LogOut className="w-4 h-4" />
              {revoking === "all" ? t("Signing out…") : t("Sign out on all devices")}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default TeamAndSessionsSetting;
